import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { KeyRoundIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { apiKeysApi } from '@/api/api-keys'
import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/keys'
import type { ApiKey, ApiKeyWithSecret, CreateApiKeyPayload } from '@/api/types'
import { ApiKeyCreatedDialog } from '@/components/api-keys/api-key-created-dialog'
import { CreateApiKeyDialog } from '@/components/api-keys/create-api-key-dialog'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { EmptyState } from '@/components/empty-state'
import { Frame } from '@/components/frame'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatDate, formatRelative } from '@/lib/format'

export const Route = createFileRoute('/_app/settings/api-keys')({
  component: ApiKeysPage,
})

function ApiKeysPage() {
  const queryClient = useQueryClient()
  const [created, setCreated] = useState<ApiKeyWithSecret | null>(null)
  const [toRevoke, setToRevoke] = useState<ApiKey | null>(null)

  const keys = useQuery({
    queryKey: queryKeys.apiKeys.list(),
    queryFn: () => apiKeysApi.list(),
  })

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all })

  const create = useMutation({
    mutationFn: (payload: CreateApiKeyPayload) => apiKeysApi.create(payload),
    onSuccess: (apiKey) => {
      void refresh()
      setCreated(apiKey)
    },
  })

  const revoke = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      void refresh()
      toast.success('Clé révoquée. Les appels qui l’utilisaient échoueront immédiatement.')
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Révocation impossible.')
    },
    onSettled: () => {
      setToRevoke(null)
    },
  })

  const items = (keys.data?.items ?? []).filter((key) => key.revoked_at === null)

  return (
    <Frame>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-pretty text-muted-foreground">
          Une clé API permet à Claude Code (MCP) et à la ligne de commande{' '}
          <code className="font-key">oo</code> d’agir en votre nom.
        </p>
        <CreateApiKeyDialog
          pending={create.isPending}
          error={create.error}
          onCreate={async (payload) => {
            try {
              await create.mutateAsync(payload)
              return true
            } catch {
              return false
            }
          }}
        />
      </div>

      {keys.isPending ? (
        <KeysSkeleton />
      ) : keys.isError ? (
        <EmptyState
          icon={KeyRoundIcon}
          title="Impossible de charger vos clés"
          description={
            keys.error instanceof ApiError ? keys.error.message : 'Réessayez dans un instant.'
          }
          action={
            <Button
              variant="outline"
              onClick={() => {
                void keys.refetch()
              }}
            >
              Réessayer
            </Button>
          }
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={KeyRoundIcon}
          title="Aucune clé API"
          description="Créez une clé pour brancher Claude Code ou la ligne de commande sur votre compte."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead>Nom</TableHead>
                <TableHead>Préfixe</TableHead>
                <TableHead>Portées</TableHead>
                <TableHead>Créée le</TableHead>
                <TableHead>Dernière utilisation</TableHead>
                <TableHead>Expiration</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((apiKey) => (
                <TableRow key={apiKey.id} data-testid="api-key-row">
                  <TableCell className="font-medium">{apiKey.name}</TableCell>
                  <TableCell>
                    <code className="font-key text-muted-foreground">{apiKey.prefix}…</code>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {apiKey.scopes.map((scope) => (
                        <Badge key={scope} variant={scope === 'write' ? 'brand' : 'muted'}>
                          {scope === 'write' ? 'Écriture' : 'Lecture'}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(apiKey.created_at)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {apiKey.last_used_at ? formatRelative(apiKey.last_used_at) : 'Jamais'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {apiKey.expires_at ? formatDate(apiKey.expires_at) : 'Aucune'}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Révoquer la clé ${apiKey.name}`}
                      onClick={() => {
                        setToRevoke(apiKey)
                      }}
                    >
                      <Trash2Icon className="text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ApiKeyCreatedDialog
        apiKey={created}
        onClose={() => {
          setCreated(null)
        }}
      />

      <ConfirmDialog
        open={toRevoke !== null}
        title="Révoquer cette clé ?"
        description={
          toRevoke
            ? `« ${toRevoke.name} » cessera immédiatement de fonctionner. Les intégrations qui l’utilisent devront recevoir une nouvelle clé.`
            : undefined
        }
        confirmLabel="Révoquer"
        destructive
        pending={revoke.isPending}
        onOpenChange={(open) => {
          if (!open && !revoke.isPending) setToRevoke(null)
        }}
        onConfirm={() => {
          if (toRevoke) revoke.mutate(toRevoke.id)
        }}
      />
    </Frame>
  )
}

function KeysSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border">
      {Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="flex items-center gap-4 border-b p-3 last:border-b-0">
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-5 w-16" />
          <Skeleton className="ml-auto h-3.5 w-28" />
        </div>
      ))}
    </div>
  )
}
